import {
  BUYBACK_FIELD as FIELD,
  BuybackFormValues,
} from '../../common/buybackTypes'

const initialOfferPercentage = 60
const initialPropertyPrice = 3_000_000
const initialOfferPrice = initialPropertyPrice * (initialOfferPercentage / 100)
// if edit don't forget set initialBaseCommissionAmount

const initialRentalFees = [
  { id: 'baseFee1', name: 'Pojištění nemovitosti', amount: 250 },
  { id: 'baseFee2', name: 'Revize a pravidelné kontroly', amount: 800 },
  { id: 'baseFee3', name: 'Daň z nemovitosti', amount: 200 },
  { id: 'baseFee4', name: 'Vodné a stočné', amount: 500 },
  { id: 'baseFee5', name: 'Vytápění', amount: 800 },
  { id: 'baseFee6', name: 'Elektřina', amount: 500 },
]
const initialFeesPrice = initialRentalFees.reduce(
  (total, fee) => total + fee.amount,
  0
)

const initialProcessCosts = [
  { id: 'processCost1', name: 'Notářské výlohy', amount: 15_000 },
  { id: 'processCost2', name: 'Advokátní výlohy', amount: 25_000 },
  // if edit don't forget summarize initialProcessCostAmountTotal below
]
const initialProcessCostAmountTotal = 40_000 // sum of process costs

const initialDebts = [
  { id: 'debt1', name: 'Exekuce', amount: 100_000 },
  // if edit don't forget summarize initialBaseDebtFeeTotal & initialDebtAmountTotal below
]
const initialBaseDebtFeeTotal = 15_000 // sum of debt fees by rules below
const initialDebtAmountTotal = 100_000 // sum of debts amounts

const initialBaseCommissionAmount = 100_000

export const FINANCING_SETTINGS = {
  investorRate: 12, // 12 % p.a.
  marketPriceAnnualGrowth: 3, // 3 % p.a.
  marketSaleCosts: 5, // 5% of the property market price
  marketSaleDurationMonths: 6,
  buybackBreakpoints: [
    12, 18, 24, 30, 36, 42, 48, 54, 60, 66, 72, 78, 84, 90, 96, 102, 108, 114,
    120,
  ],
  balanceWarningLevel: 100_000,
}

export const BUYBACK_INITIAL_VALUES: Partial<BuybackFormValues> = {
  propertyPrice: initialPropertyPrice,
  offerPercentage: initialOfferPercentage,
  offerPrice: initialOfferPrice,

  [FIELD.HouseholdMembers]: 2,

  [FIELD.RentDurationMonths]: 20,
  [FIELD.AnnualInterestRate]: 12, // 12 % p.a.

  [FIELD.RentalFees]: initialRentalFees,
  [FIELD.RentalFeesPrice]: initialFeesPrice,

  [FIELD.Debts]: initialDebts,
  [FIELD.DebtAmountTotal]: initialDebtAmountTotal,
  [FIELD.BaseDebtFeeTotal]: initialBaseDebtFeeTotal,

  [FIELD.ProcessCosts]: initialProcessCosts,
  [FIELD.ProcessCostAmountTotal]: initialProcessCostAmountTotal,

  [FIELD.BaseCommissionAmount]: initialBaseCommissionAmount,

  [FIELD.InvestorRate]: FINANCING_SETTINGS.investorRate,
  [FIELD.MarketPriceAnnualGrowth]: FINANCING_SETTINGS.marketPriceAnnualGrowth,
  [FIELD.MarketSaleCosts]: FINANCING_SETTINGS.marketSaleCosts,
  [FIELD.MarketSaleDurationMonths]: FINANCING_SETTINGS.marketSaleDurationMonths,
}

export const BUYBACK_SETTINGS = {
  rateRent: 0.05, // 5 % p.a. generated by rent
  livingMinimum: 4860, // living minimum per household member
}

/* Current version of the buyback calculator
 has to be manually updated when the calculation logic changes.
 Data saved with an old major version cannot be loaded.
 */
export const BUYBACK_CALCULATOR_VERSION = '4.0.0'

// Changelog
// 4.0.0 -- 01.12.2025 - breaking changes in financing calculations
// 3.5.0 -- 25.11.2025 - payout details, chart, UI improvements
// 3.4.1 -- 15.11.2025 - financing calculations, chart, UI improvements
// 3.4.0 -- 15.11.2025 - refactoring data types import
// 3.3.0 -- 14.11.2025 - marketBalance fixed and buybackBalance levels
// 3.2.1 -- 13.11.2025 - default annualInterestRate changed to 12%
// 3.2.0 -- 13.11.2025 - financing calculations added
// 3.1.0 -- 11.11.2025 - fees/debts/processCosts list sorting
// 3.0.0 -- 17.10.2025 - release with server/db changes
// 2.8.1 -- 17.10.2025 - fix reset form on load saved calculation
// 2.8.0 -- 16.10.2025 - serverside PDF rendering
// 2.7.0 -- 16.10.2025 - database schema created
// 2.6.1 -- 16.10.2025 - fix rounding values in form
// 2.6.0 -- 12.10.2025 - first stable version release
// 2.5.0 -- 09.10.2025 - process costs added
// 2.4.0 -- 08.10.2025 - custom commission added
// 2.3.0 -- 07.10.2025 - refactored
// 2.2.0 -- 06.10.2025 - renamed to buyback
// 2.1.0 -- 06.10.2025 - business PDF
// 2.0.0 -- 01.10.2025 - first public release
// 1.3.0 -- 01.10.2025 - added price fetching, dynamic fields etc...
// 1.2.1 -- 17.09.2025 - fix img in pdf
// 1.2.0 -- 16.09.2025 - export PDF
// 1.1.0 -- 16.09.2025 - barely working multistep formexport PDF
// 1.0.0 -- 02.09.2025 - initial simple version
// 0.0.0 -- 28.08.2025 - started

function parseVersion(
  version?: string | null
): { major: number; minor: number; patch: number } | null {
  if (!version) return null
  const match = String(version)
    .trim()
    .match(/^(\d+)\.(\d+)\.(\d+)$/)
  if (!match) return null
  return { major: +match[1], minor: +match[2], patch: +match[3] }
}

export function isBuybackVersionCompatible(
  savedVersion?: string | null
): boolean {
  const current = parseVersion(BUYBACK_CALCULATOR_VERSION)
  const old = parseVersion(savedVersion)

  if (!current || !old) return false
  return current.major === old.major
}

export const UNIT_DEPENDS_FORWARD: Record<string, FIELD[]> = {
  [FIELD.PriceEstimateId]: [
    FIELD.EstimatedPrice,
    FIELD.PriceEstimateDate,
    FIELD.PropertyPrice,
    FIELD.OfferPrice,
    FIELD.AnnualInterestPrice,
    FIELD.UnitDisposition,
    FIELD.UnitArea,
    FIELD.UnitUtility,
    FIELD.ConstructionType,
    FIELD.RentLocalPrice,
    FIELD.BaseCommissionAmount,
    FIELD.RentPrice, // baseline z Offer
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.PropertyPrice]: [
    FIELD.OfferPrice,
    FIELD.AnnualInterestPrice,
    FIELD.BaseCommissionAmount,
    FIELD.RentPrice, // baseline z Offer
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.OfferPrice]: [
    FIELD.OfferPercentage,
    FIELD.AnnualInterestPrice,
    FIELD.BaseCommissionAmount,
    FIELD.RentPrice, // baseline z Offer
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.OfferPercentage]: [
    FIELD.OfferPrice,
    FIELD.AnnualInterestPrice,
    FIELD.BaseCommissionAmount,
    FIELD.RentPrice, // baseline z Offer
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.AnnualInterestRate]: [
    FIELD.AnnualInterestPrice,
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.UnitDisposition]: [
    FIELD.RentLocalPrice,
    FIELD.RentTotalPrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.UnitArea]: [
    FIELD.RentLocalPrice,
    FIELD.RentTotalPrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.UnitUtility]: [
    FIELD.RentLocalPrice,
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice, // ?
    FIELD.BuybackPrice,
  ],

  [FIELD.RentalFees]: [FIELD.RentTotalPrice, FIELD.RentalFeesPrice],

  [FIELD.Debts]: [FIELD.DebtAmountTotal],

  [FIELD.RentDurationMonths]: [
    FIELD.BaseBuybackPrice,
    FIELD.BuybackPrice,
    FIELD.RentTotalPrice,
  ],

  [FIELD.RentPrice]: [FIELD.RentTotalPrice, FIELD.BuybackPrice],

  [FIELD.BuybackPrice]: [FIELD.RentTotalPrice],
}

export const BUILDING_DEPENDS_FORWARD: Record<string, FIELD[]> = {
  [FIELD.PriceEstimateId]: [
    FIELD.EstimatedPrice,
    FIELD.PriceEstimateDate,
    FIELD.PropertyPrice,
    FIELD.OfferPrice,
    FIELD.AnnualInterestPrice,
    FIELD.BuildingLandArea,
    FIELD.BuildingArea,
    FIELD.BuildingUtility,
    FIELD.ConstructionType,
    FIELD.RentLocalPrice,
    FIELD.BaseCommissionAmount,
    FIELD.RentPrice, // baseline z Offer
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.PropertyPrice]: [
    FIELD.OfferPrice,
    FIELD.AnnualInterestPrice,
    FIELD.BaseCommissionAmount,
    FIELD.RentPrice, // baseline z Offer
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.OfferPrice]: [
    FIELD.OfferPercentage,
    FIELD.AnnualInterestPrice,
    FIELD.BaseCommissionAmount,
    FIELD.RentPrice, // baseline z Offer
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.OfferPercentage]: [
    FIELD.OfferPrice,
    FIELD.AnnualInterestPrice,
    FIELD.BaseCommissionAmount,
    FIELD.RentPrice, // baseline z Offer
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.AnnualInterestRate]: [
    FIELD.AnnualInterestPrice,
    FIELD.RentTotalPrice,
    FIELD.BaseBuybackPrice,
    FIELD.RentBasePrice,
    FIELD.BuybackPrice,
  ],

  [FIELD.RentalFees]: [FIELD.RentTotalPrice, FIELD.RentalFeesPrice],

  [FIELD.Debts]: [FIELD.DebtAmountTotal],

  [FIELD.RentDurationMonths]: [
    FIELD.BaseBuybackPrice,
    FIELD.BuybackPrice,
    FIELD.RentTotalPrice,
  ],

  [FIELD.RentPrice]: [FIELD.RentTotalPrice, FIELD.BuybackPrice],

  [FIELD.BuybackPrice]: [FIELD.RentTotalPrice],
}
